import { MAJOR_ARCANA, getCardById } from './majorArcana';
import { getSpread } from './spreads';

export function shuffleDeck(seed) {
  const deck = [...MAJOR_ARCANA];
  let s = seed ?? Date.now();
  for (let i = deck.length - 1; i > 0; i -= 1) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

export function parseReadingFromQuery(spreadId, cardsParam) {
  if (!spreadId || !cardsParam) return null;
  const spread = getSpread(spreadId);
  const ids = cardsParam.split('-').map((n) => parseInt(n, 10)).filter((n) => !isNaN(n));
  if (ids.length !== spread.cardCount) return null;
  const cards = ids.map(getCardById).filter(Boolean);
  if (cards.length !== spread.cardCount) return null;
  return buildReading(spreadId, cards);
}

export function buildReadingUrl(spreadId, cardIds) {
  const ids = cardIds.join('-');
  return `/tarot?spread=${spreadId}&cards=${ids}`;
}

function reduceToTwoDigits(n) {
  let x = Math.abs(n);
  while (x > 99) {
    x = String(x).split('').reduce((a, d) => a + parseInt(d, 10), 0);
  }
  return String(x).padStart(2, '0');
}

export function deriveLuckyNumbers(cards) {
  const set = new Set();
  cards.forEach((card) => {
    card.luckyDigits.forEach((d) => set.add(d));
    set.add(reduceToTwoDigits(card.number));
    set.add(reduceToTwoDigits(card.number * 7 + 3));
  });
  const sum = cards.reduce((a, c) => a + c.number, 0);
  set.add(reduceToTwoDigits(sum));
  set.add(String(sum % 1000).padStart(3, '0'));
  return [...set].slice(0, 8);
}

function buildPositionInsight(card, position, spreadId) {
  if (spreadId === 'lotto') {
    if (position.key === 'luck') {
      return `พลังโชคจาก "${card.nameTh}" (${card.upright}) — ช่วงนี้พลังงาน${card.element}สนับสนุนโชคลาภ โดยเฉพาะเลขที่เกี่ยวข้องกับเลขไพ่ ${card.number}`;
    }
    if (position.key === 'block') {
      return `สิ่งกีดขวาง: ${card.keywords.join(', ')} — ${card.advice} ระวัง${card.money.toLowerCase().includes('ระวัง') ? card.money : 'การตัดสินใจเร็วเกินไป'}`;
    }
    return `เลขนำทางจาก "${card.nameTh}": จักรวาลชี้เลข ${card.luckyDigits.join(', ')} และคำทำนาย "${card.dreamKeyword}" สอดคล้องกับพลังไพ่`;
  }
  return `${position.labelTh}: "${card.nameTh}" — ${card.upright}`;
}

export function buildReading(spreadId, cards) {
  const spread = getSpread(spreadId);
  const positions = spread.positions.map((pos, i) => ({
    ...pos,
    card: cards[i],
    insight: buildPositionInsight(cards[i], pos, spreadId),
  }));

  const luckyNumbers = deriveLuckyNumbers(cards);
  const primaryDream = cards[cards.length - 1]?.dreamKeyword ?? cards[0]?.dreamKeyword;

  const synthesis = spreadId === 'lotto'
    ? `จากไพ่ ${cards.map((c) => c.nameTh).join(' · ')} พลังโชคสัมพันธ์กับเลข ${luckyNumbers.slice(0, 4).join(', ')} แนะนำค้นหาความฝัน "${primaryDream}" บน Hotto เพื่อเช็คเลขเด็ดที่สอดคล้องกับไพ่ของคุณ`
    : `ภาพรวมจากไพ่ ${cards.map((c) => c.nameTh).join(' → ')}: ${cards.map((c) => c.advice).join(' ')}`;

  return {
    spreadId,
    spread,
    positions,
    luckyNumbers,
    primaryDream,
    synthesis,
    sharePath: buildReadingUrl(spreadId, cards.map((c) => c.id)),
  };
}

export function drawReading(spreadId, pickedIds) {
  const spread = getSpread(spreadId);
  const cards = pickedIds.slice(0, spread.cardCount).map(getCardById).filter(Boolean);
  if (cards.length !== spread.cardCount) return null;
  return buildReading(spreadId, cards);
}
